import React, { useEffect, useContext, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import Footer from "../Footer";
import TherapistSidebar from "../TherapistSidebar";
import axiosInstance from "../../api/axiosInstance";
import allowedUserIds from "../../pages/admin/adminConfig";
import beach from "../../assets/images/beach.png";
import Env from "../../runtimeEnv";

function AIDevTestPage() {
  const navigate = useNavigate();
  const [activeNavItem, setActiveNavItem] = useState("test-room");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [chatSummaries, setChatSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [clearSessionLoading, setClearSessionLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const formatMarkdownText = (text) => {
    if (!text) return "No summary available";

    return text
      .replace(
        /\*\*([^:]*?):\*\*/g,
        '<h4 class="text-lg font-semibold mt-6 mb-3 text-gray-800">$1:</h4>'
      )
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/^\d+\.\s(.+)$/gm, '<li class="ml-4 mb-2 text-gray-700">$1</li>')
      .replace(/^-\s(.+)$/gm, '<li class="ml-4 mb-2 text-gray-700">$1</li>')
      .replace(/(<li.*?<\/li>\s*)+/g, (match) => {
        if (match.includes("ml-4")) {
          return `<ol class="list-decimal ml-6 mb-4 space-y-1">${match}</ol>`;
        }
        return match;
      })
      .replace(/\n\n/g, '</p><p class="mb-4 text-gray-700">')
      .replace(/\n/g, "<br>")
      .replace(/^(.+)/, '<p class="mb-4 text-gray-700">$1')
      .replace(/(.+)$/, "$1</p>")
      .replace(/<\/p>\s*<h4/g, "</p><h4")
      .replace(/<\/h4>\s*<p/g, "</h4><p")
      .replace(/<p[^>]*><\/p>/g, "")
      .replace(/<br>\s*<h4/g, "<h4")
      .replace(/<\/h4>\s*<br>/g, "</h4>")
      .replace(/<br>\s*<ol/g, "<ol")
      .replace(/<\/ol>\s*<br>/g, "</ol>");
  };

  const {
    currentNickname,
    PlutonicuserID,
    userType,
    loading: authLoading,
    handleLogout,
    setCurrentFormType,
  } = useContext(AuthContext);

  useEffect(() => {
    if (authLoading) return;

    const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");

    if (!allowedUserIds.includes(userId)) {
      console.warn("AIDevTestPage.js: warn event");
      navigate("/therapy-rooms");
    }
  }, [authLoading, PlutonicuserID, navigate]);

  useEffect(() => {
    const updateSidebarForScreen = () => {
      if (typeof window !== "undefined") {
        setSidebarCollapsed(window.innerWidth < 1024);
      }
    };
    if (userType === "Client") setSidebarCollapsed(true);
    updateSidebarForScreen();
    window.addEventListener("resize", updateSidebarForScreen);
    return () => window.removeEventListener("resize", updateSidebarForScreen);
  }, [userType]);

  useEffect(() => {
    if (activeNavItem === "summaries" && !authLoading) {
      fetchChatSummaries();
    }
  }, [activeNavItem, authLoading]);


  const fetchChatSummaries = async () => {
    setSummaryLoading(true);
    try {
      const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
      const response = await axiosInstance.get(`/api/test-summarize/${userId}`);
      
      if (response.data && response.data.summaries) {
        const summaries = response.data.summaries.map(summary => ({
          sessionID: summary.sessionID,
          sessionName: summary.sessionName,
          summary: summary.summary,
          timestamp: summary.timestamp,
          chatMessageCount: summary.chatMessageCount,
          dataTypes: summary.dataTypes || {},
          testType: summary.testType,
          model: summary.model,
          temperature: summary.temperature,
          freshSummaryGenerated: summary.freshSummaryGenerated
        }));

        setChatSummaries(summaries);
        setLastUpdated(new Date().toLocaleString());
      }
    } catch (error) {
      console.error("AIDevTestPage.js: error event");
    } finally {
      setSummaryLoading(false);
    }
  };

  const triggerTestSummarization = async () => {
    setLoading(true);
    try {
      const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
      await axiosInstance.post(`/api/test-summarize/${userId}`);
      await fetchChatSummaries();
    } catch (error) {
      console.error("AIDevTestPage.js: error event");
      alert("Failed to trigger test summarization. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const clearSession = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to clear all test session data? This will remove chat history, whiteboard data, object placements, and AI summaries."
    );
    
    if (!confirmed) return;

    setClearSessionLoading(true);
    try {
      const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
      const sessionName = `beach-ai-dev-test-meeting-${userId}`;
      
      await axiosInstance.post(`/api/clear-test-session`, {
        userId,
        sessionName
      });
      
      setChatSummaries([]);
      alert("Test session data cleared successfully!");
    } catch (error) {
      console.error("AIDevTestPage.js: error event");
      alert("Failed to clear test session data. Please try again.");
    } finally {
      setClearSessionLoading(false);
    }
  };

  const handleEnterTestRoom = async () => {
    const userId = PlutonicuserID || localStorage.getItem("PlutonicuserID");
    if (!userId) {
      alert("User ID is missing. Please log in again.");
      return;
    }

    const testMeetingID = `beach-ai-dev-test-meeting-${userId}`;
    
    try {
      await axiosInstance.post(`/api/create-ai-dev-test-session`, {
        userID: userId
      });
    } catch (error) {
      console.error("AIDevTestPage.js: error event");
    }
    
    sessionStorage.setItem("meetingID", testMeetingID);
    sessionStorage.setItem("userId", userId);
    //sessionStorage.setItem("isTestRoom", "true");

    navigate("/unity-room/test-ai-dev");
  };

  // if (authLoading) {
  //   return (
  //     <div
  //       style={{
  //         height: "100vh",
  //         display: "flex",
  //         justifyContent: "center",
  //         alignItems: "center",
  //       }}
  //     >
  //       <p>Loading...</p>
  //     </div>
  //   );
  // }

  return (
    <div>
      <Navbar />
      {userType === "Therapist" && (
        <TherapistSidebar
          activeNavItem={activeNavItem}
          setActiveNavItem={setActiveNavItem}
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
        />
      )}

      <div
        className={`min-h-screen pt-32 pb-16 px-12 transition-all duration-300 ${
          userType === "Therapist" ? (sidebarCollapsed ? "ml-16" : "ml-64") : ""
        }`}
        style={{
          background: "linear-gradient(to bottom, #CBCECE, #77D8EE, #CBCECE)",
        }}
      >
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl font-bold">🧪 AI Dev Test Environment</h1>
            <div className="text-sm text-gray-600">
              {lastUpdated && `Last updated: ${lastUpdated}`}
            </div>
          </div>

          <div className="flex space-x-4 mb-8">
            <button
              onClick={() => setActiveNavItem("test-room")}
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeNavItem === "test-room"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              Test Room Access
            </button>
            <button
              onClick={() => setActiveNavItem("summaries")}
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeNavItem === "summaries"
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              Chat Summaries
            </button>
          </div>

          {activeNavItem === "test-room" && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold mb-6">
                AI Test Room - Beach Environment
              </h2>

              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <img
                    src={beach}
                    alt="Beach AI Test Room"
                    className="w-full rounded-lg shadow-md"
                  />
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">
                    Test Room Features
                  </h3>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      AI Companion Interaction
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Beach Environment Testing
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Chat History Recording
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Whiteboard Data Tracking
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Object Placement Analysis
                    </li>
                    <li className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      Comprehensive Summary Generation
                    </li>
                  </ul>

                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> This is a development test
                      environment. All interactions are recorded for testing and
                      evaluation purposes.
                    </p>
                  </div>

                  <button
                    onClick={handleEnterTestRoom}
                    disabled={loading}
                    className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors"
                  >
                    {loading ? "Connecting..." : "Enter AI Test Room"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeNavItem === "summaries" && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Test Room Chat Summaries</h2>
                <div className="space-x-4">
                  <button
                    onClick={clearSession}
                    disabled={clearSessionLoading}
                    className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    {clearSessionLoading ? "Processing..." : "Clear Session"}
                  </button>
                  <button
                    onClick={triggerTestSummarization}
                    disabled={loading}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    {loading ? "Processing..." : "Generate Fresh Test Summary"}
                  </button>
                </div>
              </div>

              {summaryLoading ? (
                <div className="text-center py-8">
                  <p>Loading summaries...</p>
                </div>
              ) : chatSummaries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No AI dev test summaries available.</p>
                  <p className="text-sm mt-2">
                    Enter the AI test room and interact with the AI companion,
                    then generate fresh test summaries to analyze the
                    conversation.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {chatSummaries.map((summary, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-6"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold">
                            {summary.sessionName ||
                              `Session ${summary.sessionID}`}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Generated:{" "}
                            {new Date(summary.timestamp).toLocaleString()}
                          </p>
                          {summary.chatMessageCount && (
                            <p className="text-sm text-gray-500">
                              Chat Messages: {summary.chatMessageCount}
                            </p>
                          )}
                          {summary.sessionDate && (
                            <p className="text-sm text-gray-500">
                              Session Date:{" "}
                              {new Date(summary.sessionDate).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {summary.dataTypes?.chat && (
                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                              Chat
                            </span>
                          )}
                          {summary.dataTypes?.whiteboard && (
                            <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                              Whiteboard
                            </span>
                          )}
                          {summary.dataTypes?.objects && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                              Objects
                            </span>
                          )}
                          {summary.testType && (
                            <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                              {summary.testType}
                            </span>
                          )}
                          {summary.model && (
                            <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded">
                              {summary.model}
                            </span>
                          )}
                          {summary.temperature && (
                            <span className="bg-teal-100 text-teal-800 text-xs px-2 py-1 rounded">
                              temp: {summary.temperature}
                            </span>
                          )}
                          {summary.freshSummaryGenerated && (
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">
                              ✨ Fresh
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="prose max-w-none">
                        <div
                          className="text-gray-700"
                          dangerouslySetInnerHTML={{
                            __html: formatMarkdownText(summary.summary),
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default AIDevTestPage;
